import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonRow,
  IonSpinner,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { sendSharp } from 'ionicons/icons';

import { Store } from '@ngxs/store';

import { Subscription } from 'rxjs';

import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { FeedbackService } from 'projects/tools/src/lib/shared-module/feedback/feedback.service';
import { FeedbackFeatureEnum } from 'projects/tools/src/lib/shared-module/feedback/feedback-feature.enum';

import {
  getSeededNickname,
  adjectives,
  nouns,
} from 'projects/tools/src/lib/shared-module/leaderboard/badwords-nickname.pipe';

import * as LeaderboardNicknameActions from 'projects/tools/src/lib/shared-module/leaderboard/store/leaderboard-nickname.actions';

import { LeaderboardTabsComponent } from 'projects/tools/src/lib/shared-module/leaderboard-tabs/leaderboard-tabs.component'; // lib-app-leaderboard-tabs
import { TranslateModule } from '@ngx-translate/core';
import { LeaderboardNicknameValidatorDirective } from '../helpers/leaderboard-nickname-validator.directive';

@Component({
  selector: 'lib-app-leaderboard-win-dialog',
  templateUrl: './leaderboard-win-dialog.component.html',
  styleUrls: ['./leaderboard-win-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonButton,
    IonButtons,
    IonCol,
    IonGrid,
    IonIcon,
    IonInput,
    IonItem,
    IonRow,
    IonSpinner,
    LeaderboardTabsComponent,
    TranslateModule,
    LeaderboardNicknameValidatorDirective,
  ],
})
export class LeaderboardWinDialogComponent implements OnInit, OnDestroy {
  @ViewChild('submitbutton', { read: ElementRef }) submitButton: ElementRef;

  private lifecycleSubscriptions: Subscription[];

  public submitInProgress = false;
  public smallProgress = false;

  public nickname = '';
  seededNickname = '';

  constructor(public leaderboard: LeaderboardService, private feedback: FeedbackService, private store: Store) {
    addIcons({ sendSharp });
  }

  splitAtSecondCapital(str: string): [string, string] {
    // 1) find all positions of capital letters A–Z (add ÄÖÜ if needed)
    const caps = [...str.matchAll(/[A-ZÄÖÜ]/g)].map((m) => m.index!);
    // 2) if there aren’t at least two capitals, just return the whole string
    if (caps.length < 2) {
      return [str, ''];
    }
    // 3) slice just before the 2nd capital
    const splitPos = caps[1];
    return [str.slice(0, splitPos), str.slice(splitPos)];
  }

  nicknameIsSeeded() {
    // Quick if this is generated newly.
    if (this.nickname === this.seededNickname) {
      return true;
    }

    // We need to check if this nickname is seeded by us.
    // If we have a number at the end, we need to remove it.
    const temp_nickname = this.nickname.replace(/[0-9]/g, '');
    // Split the word into 2 parts. Both are starting with a capital letter, all others are lowercase.
    const nickname_parts = this.splitAtSecondCapital(temp_nickname);

    // Check if the parts are in adjectives and nouns list
    return adjectives.includes(nickname_parts[0]) && nouns.includes(nickname_parts[1]);
  }

  ngOnInit() {
    this.lifecycleSubscriptions = [
      this.leaderboard.leaderboardContext$.subscribe((leaderboardContext) => {
        if (leaderboardContext && leaderboardContext.entry) {
          setTimeout(() => {
            this.feedback.maybePromptFeedbackModal(FeedbackFeatureEnum.Leaderboard);
          }, 10000);
        }
      }),
    ];

    this.store
      .selectOnce((state) => state.leaderboardNicknameState)
      .subscribe((nickname) => {
        if (!nickname || nickname === '') {
          getSeededNickname(`${Math.floor(Math.random() * 1000000)}`).then((nickname) => {
            this.seededNickname = nickname;
            this.nickname = nickname;
          });
        } else {
          this.nickname = nickname;
        }
      });

    this.smallProgress = true;
    this.leaderboard.updateLeaderboardAssociation().then((_) => {
      this.smallProgress = false;
    });
  }

  ngOnDestroy(): void {
    this.lifecycleSubscriptions.forEach(
      (subscription) => subscription && subscription.unsubscribe && subscription.unsubscribe()
    );
  }

  async sendToLeaderboard() {
    if (this.submitButton.nativeElement.disabled) {
      return;
    }
    this.submitInProgress = true;
    this.store.dispatch(new LeaderboardNicknameActions.SetLeaderboardNickname({ nickname: this.nickname }));
    await this.leaderboard.newLeaderboardEntry(this.nickname);
    this.submitInProgress = false;
  }

  setName(value: string) {
    this.nickname = value;
  }

  getErrorText(errors: any): string | null {
    if (errors) {
      if (errors.profanity) {
        return 'PUZZLE.leaderboard_win_dialog.nickname_profanity';
      }
      if (errors.profaneEmoji) {
        return 'PUZZLE.leaderboard_win_dialog.nickname_profane_emoji';
      }
      if (errors.empty) {
        return 'PUZZLE.leaderboard_win_dialog.nickname_required';
      }
      if (errors.minLength) {
        return 'PUZZLE.leaderboard_win_dialog.nickname_minlength';
      }
      if (errors.maxLength) {
        return 'PUZZLE.leaderboard_win_dialog.nickname_maxlength';
      }
      if (errors.specialChars) {
        return 'PUZZLE.leaderboard_win_dialog.nickname_specialChars';
      }
    }
    return null;
  }
}
